import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import Footer from '../../components/layout/Footer';
import { NamedGrayIcon } from '../../components/icons/GrayIconBox';
import './FeatureLanding.css';
import { TEACHER_COUNT_LABEL } from '../../constants/siteStats';
import { PptEditDemo } from '../../components/demo';

const AiPptLandingPage = () => {
  const navigate = useNavigate();

  const faqData = [
    {
      q: 'AI로 만든 PPT를 수정할 수 있나요?',
      a: '네, 만든 자리에서 글자를 눌러 바로 고칠 수 있어요. 글자형 PPT는 PPTX로 받아 PowerPoint, Google Slides에서도 고칠 수 있고, 디자인형은 슬라이드가 그림이라 왓퀴즈 화면에서 고쳐요.'
    },
    {
      q: '교육과정에 맞는 PPT가 자동으로 나오나요?',
      a: '네, 왓퀴즈 AI는 교과 내용과 학년 수준에 맞춰 슬라이드를 구성해요. 교과서 PDF를 첨부하면 해당 내용을 기반으로 더 정확한 PPT를 생성해요.'
    },
    {
      q: 'PPT에 이미지도 포함되나요?',
      a: '네, 주제에 맞는 그림을 넣고 디자인도 AI가 골라요. 마음에 들지 않는 그림은 "3번 장 그림 바꿔줘"처럼 말하면 바꿔요.'
    },
    {
      q: '한 번에 몇 장까지 생성되나요?',
      a: '"10장"처럼 장수를 적으면 그만큼, 적지 않으면 분량에 맞춰 정해요. 무료는 8장, PRO는 40장까지예요. 슬라이드 구성을 먼저 보고 빼거나 더할 수 있어요.'
    },
    {
      q: '무료로 사용할 수 있나요?',
      a: '네, 가입만 하면 글자형 PPT를 8장까지 만들어 PPTX로 받을 수 있고 디자인 PPT도 한 번 체험할 수 있어요. 디자인 PPT와 40장까지 만들기는 PRO 구독(월 16,667원~)으로 이용하세요.'
    },
    {
      q: '미리캔버스와 뭐가 다른가요?',
      a: '미리캔버스는 디자인 템플릿 도구이고, 왓퀴즈는 교사 전용 AI 플랫폼이에요. 왓퀴즈는 PPT 외에도 생기부 작성, 학습지 생성, 수행평가 채점까지 교사 업무를 올인원으로 지원해요.'
    }
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "SoftwareApplication",
        "name": "왓퀴즈 AI PPT 만들기",
        "applicationCategory": "EducationalApplication",
        "operatingSystem": "Web",
        "url": "https://whatquiz.co.kr/ai-ppt",
        "description": "교사 전용 AI PPT 만들기. 주제만 적으면 슬라이드 구성을 먼저 보여 주고, 만든 PPT는 글자를 바로 고칠 수 있어요.",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "KRW",
          "description": "무료로 시작, 디자인 PPT는 PRO"
        },
        "creator": {
          "@type": "Organization",
          "name": "왓퀴즈",
          "url": "https://whatquiz.co.kr"
        }
      },
      {
        "@type": "HowTo",
        "name": "AI로 수업 PPT 만드는 방법",
        "description": "왓퀴즈에서 AI로 수업용 PPT를 만드는 3단계 방법",
        "step": [
          {
            "@type": "HowToStep",
            "position": 1,
            "name": "주제 입력",
            "text": "수업 주제와 학년을 적어요. PRO는 교과서 PDF, 한글 파일을 첨부해 그 내용으로 만들 수 있어요."
          },
          {
            "@type": "HowToStep",
            "position": 2,
            "name": "슬라이드 구성 확인",
            "text": "AI가 보여 준 슬라이드 구성을 보고 빼거나 더할 내용을 말해요."
          },
          {
            "@type": "HowToStep",
            "position": 3,
            "name": "고치고 받기",
            "text": "만든 PPT에서 글자를 바로 고치고, 글자형은 PPTX로 받아 PowerPoint에서도 고칠 수 있어요."
          }
        ]
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqData.map(faq => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.a
          }
        }))
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "왓퀴즈", "item": "https://whatquiz.co.kr" },
          { "@type": "ListItem", "position": 2, "name": "AI PPT 만들기", "item": "https://whatquiz.co.kr/ai-ppt" }
        ]
      }
    ]
  };

  return (
    <div className="feature-landing">
      <Helmet>
        <title>AI PPT 만들기 | 주제만 적으면 수업 PPT | 교사 전용 무료 | 왓퀴즈</title>
        <meta name="description" content={`수업 주제만 적으면 AI가 수업용 PPT를 만들어요. 슬라이드 구성 미리보기, 글자 바로 고치기, PPTX 받기. ${TEACHER_COUNT_LABEL} 교사가 검증한 왓퀴즈에서 무료로 시작하세요.`} />
        <meta name="keywords" content="AI PPT 만들기, 수업 PPT 만들기, 교사 PPT, AI PPT 자동 생성, 수업자료 만들기, AI 프레젠테이션, 교사용 AI PPT, PPT 자동 생성, 수업 자료 AI, 무료 PPT 만들기" />
        <link rel="canonical" href="https://whatquiz.co.kr/ai-ppt" />
        <meta name="robots" content="index, follow" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://whatquiz.co.kr/ai-ppt" />
        <meta property="og:title" content="AI PPT 만들기 | 주제만 적으면 수업 PPT | 왓퀴즈" />
        <meta property="og:description" content="수업 주제만 적으면 AI가 수업용 PPT를 만들어요. 글자를 바로 고치고 PPTX로 받아요. 교사 전용 무료." />
        <meta property="og:image" content="https://whatquiz.co.kr/og-image-whatquiz-2025.png" />
        <meta property="og:locale" content="ko_KR" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI PPT 만들기 | 주제만 적으면 수업 PPT | 왓퀴즈" />
        <meta name="twitter:description" content="수업 주제만 적으면 AI가 수업용 PPT를 만들어요. 교사 전용 무료." />

        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>

      {/* 히어로 */}
      <section className="feature-hero" style={{ background: 'linear-gradient(180deg, #eff6ff 0%, #fff 100%)' }}>
        <div className="feature-hero-inner">
          <span className="feature-hero-badge" style={{ background: '#dbeafe', color: '#1d4ed8' }}>
            교사 전용 AI
          </span>
          <h1>주제만 적으면<br /><em>수업 PPT</em>가 나와요</h1>
          <p className="feature-hero-desc">
            슬라이드 구성을 먼저 보고, 만든 자리에서 글자를 바로 고쳐요.<br />
            글자형은 PPTX로 받아 파워포인트에서도 고칠 수 있어요.
          </p>
          <button
            className="feature-hero-cta"
            onClick={() => navigate('/chat?category=ppt_maker')}
          >
            무료로 PPT 만들기 →
          </button>
        </div>
      </section>

      <section className="feature-section feature-section--demo">
        <div className="feature-section-inner feature-demo">
          <PptEditDemo surface="ai_ppt" title="AI PPT" />
        </div>
      </section>

      {/* GEO 핵심 답변 */}
      <section className="feature-section" style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', clipPath: 'inset(50%)', whiteSpace: 'normal', border: 0 }}>
        <div className="feature-section-inner">
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '28px' }}>
            <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 700, color: '#1d4ed8' }}>핵심 답변</p>
            <h2 style={{ margin: '0 0 12px', fontSize: '22px', fontWeight: 800, color: '#111' }}>AI PPT 만들기란?</h2>
            <p style={{ margin: '0 0 20px', fontSize: '16px', lineHeight: 1.75, color: '#334155' }}>
              AI PPT 만들기는 수업 주제, 교과 내용, 첨부 자료를 바탕으로 AI가 슬라이드 목차와 내용을 구성하고 PPTX 파일 생성을 돕는 방식이에요. 왓퀴즈는 교사가 슬라이드 구성을 먼저 확인해 고치고, 만든 PPT의 글자를 바로 고친 뒤 글자형(PPTX) 또는 디자인형으로 받을 수 있게 해요.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '12px' }}>
              {[
                { label: '입력 자료', value: '수업 주제 (PRO: 교과서 PDF, HWP, 이미지 첨부)' },
                { label: '출력 형태', value: '글자형 PPTX, 디자인형 PPT' },
                { label: '교사 제어', value: '구성 미리보기, 글자 바로 고치기' },
              ].map((item) => (
                <div key={item.label} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '16px' }}>
                  <strong style={{ display: 'block', fontSize: '13px', color: '#64748b', marginBottom: '6px' }}>{item.label}</strong>
                  <span style={{ fontSize: '14px', color: '#111', lineHeight: 1.5 }}>{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 3단계 사용법 */}
      <section className="feature-section feature-section--gray">
        <div className="feature-section-inner">
          <h2 className="feature-section-title">AI로 PPT를 어떻게 만드나요?</h2>
          <p className="feature-section-subtitle">디자인 작업 없이 3단계면 돼요</p>
          <div className="feature-steps">
            <div className="feature-step">
              <div className="feature-step-number" style={{ background: '#3182f6' }}>1</div>
              <h3>주제 입력</h3>
              <p>"중2 과학 광합성 10장"처럼 주제와 학년을 적어요. PRO는 교과서 PDF나 한글 파일을 첨부할 수 있어요.</p>
            </div>
            <div className="feature-step">
              <div className="feature-step-number" style={{ background: '#3182f6' }}>2</div>
              <h3>슬라이드 구성 확인</h3>
              <p>AI가 보여 준 구성을 보고 빼거나 더할 내용을 말해요.</p>
            </div>
            <div className="feature-step">
              <div className="feature-step-number" style={{ background: '#3182f6' }}>3</div>
              <h3>바로 고치고 받기</h3>
              <p>만든 PPT에서 글자를 눌러 고치고, PPTX로 받거나 발표 모드로 띄워요.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 왓퀴즈 PPT 특징 */}
      <section className="feature-section">
        <div className="feature-section-inner">
          <h2 className="feature-section-title">왓퀴즈 AI PPT, 뭐가 다를까요?</h2>
          <p className="feature-section-subtitle">교사가 만든 교사를 위한 AI PPT 도구예요</p>
          <div className="feature-grid">
            <div className="feature-card">
              <div className="feature-card-icon"><NamedGrayIcon name="book" /></div>
              <h3>교과 내용 기반 생성</h3>
              <p>교과서 PDF를 첨부하면 그 내용으로 수업 흐름에 맞는 PPT를 만들어요. (PRO)</p>
            </div>
            <div className="feature-card">
              <div className="feature-card-icon"><NamedGrayIcon name="palette" /></div>
              <h3>글자형과 디자인형</h3>
              <p>글자형은 파워포인트에서 고칠 수 있는 PPTX로, 디자인형은 그림처럼 꾸민 슬라이드로 받아요.</p>
            </div>
            <div className="feature-card">
              <div className="feature-card-icon"><NamedGrayIcon name="crayon" /></div>
              <h3>만든 자리에서 바로 고치기</h3>
              <p>글자를 눌러 고치거나 "3번 장 그림 바꿔줘"처럼 말하면 그 장만 바뀌어요.</p>
            </div>
            <div className="feature-card">
              <div className="feature-card-icon"><NamedGrayIcon name="file" /></div>
              <h3>다양한 파일 첨부</h3>
              <p>PDF, 한글(HWP), 엑셀, 이미지로 된 수업 자료를 첨부하면 그 내용을 반영해요. (PRO)</p>
            </div>
          </div>
        </div>
      </section>

      {/* 비교표 */}
      <section className="feature-section feature-section--gray">
        <div className="feature-section-inner">
          <h2 className="feature-section-title">AI PPT 도구 비교</h2>
          <p className="feature-section-subtitle">교사에게 맞는 AI PPT 도구를 찾고 계신가요?</p>
          <div className="feature-compare">
            <table>
              <thead>
                <tr>
                  <th>비교 항목</th>
                  <th>수작업</th>
                  <th>미리캔버스</th>
                  <th className="highlight-col">왓퀴즈</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>교과 내용 반영</strong></td>
                  <td>직접 작성</td>
                  <td>템플릿 활용</td>
                  <td className="highlight-col">AI 자동 분석</td>
                </tr>
                <tr>
                  <td><strong>생기부·채점 연계</strong></td>
                  <td>없음</td>
                  <td>없음</td>
                  <td className="highlight-col">올인원 지원</td>
                </tr>
                <tr>
                  <td><strong>가격</strong></td>
                  <td>무료</td>
                  <td>교육용 무료/유료</td>
                  <td className="highlight-col">무료로 시작</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="feature-section">
        <div className="feature-section-inner">
          <h2 className="feature-section-title">자주 묻는 질문</h2>
          <div className="feature-faq-list">
            {faqData.map((faq, idx) => (
              <div key={idx} className="feature-faq-item">
                <h3 className="feature-faq-q">{faq.q}</h3>
                <p className="feature-faq-a">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="feature-bottom-cta">
        <h2>다음 수업 PPT,<br />주제 한 줄로 시작해 보세요</h2>
        <p>가입만 하면 무료로 시작할 수 있어요</p>
        <div className="feature-bottom-cta-buttons">
          <button className="feature-cta-primary" onClick={() => navigate('/chat?category=ppt_maker')}>
            무료로 PPT 만들기
          </button>
          <button className="feature-cta-secondary" onClick={() => navigate('/pricing')}>
            요금제 보기
          </button>
        </div>
      </section>

      {/* 관련 기능 */}
      <section className="feature-related">
        <div className="feature-related-inner">
          <h2>이런 기능도 있어요</h2>
          <div className="feature-related-grid">
            <div className="feature-related-card" onClick={() => navigate('/ai-worksheet')}>
              <h3>AI 학습지 만들기</h3>
              <p>OX, 서술형 등 다양한 학습지를 AI로 자동 생성</p>
            </div>
            <div className="feature-related-card" onClick={() => navigate('/ai-image')}>
              <h3>AI 이미지 생성</h3>
              <p>색칠공부, 포스터, 플래시카드 등 교육용 이미지 생성</p>
            </div>
            <div className="feature-related-card" onClick={() => navigate('/create-saenggibu')}>
              <h3>AI 생기부 작성</h3>
              <p>한 번에 30명, 2026 기재요령 반영</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AiPptLandingPage;
